import { c as _c } from "react/compiler-runtime";
import { match } from 'ts-pattern';
import { binForecast, ForecastBinBar, binForecastScale, LikelihoodBar, ForecastBinBarProps, BinForecast } from '../../chart';
import { Fmt } from '../../forecast/Fmt';
import { StyleProps } from '../../ui/uiType';
import { notNull } from '../../type';
import { useElementSize, LabelEmptyList } from '../../ui';
import { isLikelihoodValid } from '../forecastTypes';
import { resultColorFn, MultiForecastChartProps, resolutionColor, QRNoForecastsYet } from './QuestionResults';
import { scaleLinear } from 'd3-scale';
import { useSafeMantineTheme } from '@mantine/core';
import { intersectionWith, omit } from 'remeda';
import { ReactNode } from 'react';
import { QRComparisons } from './QRComparisons';
const BaselineTip = t0 => {
  const $ = _c(8);
  const {
    value,
    children
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = Fmt.fmt(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <b>{t1}</b>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <br />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t2) {
    t4 = <div className="max-w-[40em] whitespace-normal">{t2} baseline{t3}{children}</div>;
    $[5] = children;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const QRLikelihood = t0 => {
  const $ = _c(17);
  const {
    items,
    mode,
    cfg,
    parentCfg,
    showHelp: showHelpRaw,
    hideBaselineLabel,
    showScale,
    color,
    tag,
    noteCustom,
    className,
    width: widthRaw,
    comparisons
  } = t0;
  const {
    colors
  } = useSafeMantineTheme();
  const {
    width: containerWidth,
    ref: containerRef
  } = useElementSize();
  const width = widthRaw ?? containerWidth;
  const pX = mode == "text" ? 25 : 0;
  let t1;
  if ($[0] !== cfg || $[1] !== className || $[2] !== color || $[3] !== colors || $[4] !== comparisons || $[5] !== containerRef || $[6] !== hideBaselineLabel || $[7] !== items || $[8] !== mode || $[9] !== noteCustom || $[10] !== pX || $[11] !== parentCfg || $[12] !== showHelpRaw || $[13] !== showScale || $[14] !== tag || $[15] !== width) {
    const scale = width ? scaleLinear([0, 100], [pX, width - pX * 2]).nice() : undefined;
    t1 = <div ref={containerRef} className="w-full">{items.every(_temp) ? <></> : <div className={`@container relative ${className ?? ""}`}><div className="@[40em]:text-[14px] @[20em]:text-[12px] text-[10px] relative space-y-1">{tag && <div className="flex justify-end"><div>{tag}</div></div>}{match(scale).with(undefined, _temp2).otherwise(scale_0 => {
            const {
              baselineAgg,
              code,
              shouldVizRelative
            } = items[0] ?? {};
            const resColorBase = resultColorFn({
              colors,
              cfg: cfg.colors
            });
            const baseline = baselineAgg?.group ? {
              value: baselineAgg.group,
              color: baselineAgg.type == "question" ? resColorBase("question") : colors.gray[5],
              tip: match(baselineAgg.type).with("consequence-regardless", () => <BaselineTip value={baselineAgg.group}><>The forecast for this consequence regardless of regardless of the outcome of the question</></BaselineTip>).with("question", () => <BaselineTip value={baselineAgg.group}><>Baseline for the question{" "}{parentCfg && <>: <i className="text-gray-400">({parentCfg.title})</i></>}</></BaselineTip>).exhaustive()
            } : undefined;
            const layout = items.map(g => {
              const bins = binForecast({
                forecasts: g.forecasts?.map(f => match(f.cfg).when(isLikelihoodValid, t2 => {
                  const {
                    likelihood,
                    notes,
                    relativePoints
                  } = t2;
                  return {
                    ...omit(f, ["cfg"]),
                    value: likelihood,
                    valueRelative: shouldVizRelative ? relativePoints : undefined,
                    notes: notes ?? ""
                  } as BinForecast;
                }).otherwise(_temp3)).filter(notNull) ?? [],
                domain: scale_0.domain() as [number, number]
              }).bins.filter(_temp4);
              return {
                bins,
                color: g.resolution.color ?? color ?? resolutionColor(g, colors),
                label: g.resolution.label,
                labelLong: g.labelLong,
                value: g.resAgg,
                code: g.code,
                shouldVizRelative
              };
            });
            const showHelp = [showHelpRaw];
            const bgColor = code == "likelihood" ? layout[0]?.color : undefined;
            const mainValue = layout[0]?.value;
            const showBinsSameLine = layout.length == 2 && intersectionWith(layout[0].bins, layout[1].bins, _temp5).length == 0;
            const format = Fmt.getOptions({
              forecastType: "likelihood"
            });
            const binBarProps = layout.map(t3 => {
              const {
                bins: bins_0,
                color: color_0,
                value,
                code: code_0,
                labelLong,
                label
              } = t3;
              return {
                key: code_0 ?? "_",
                value,
                bins: bins_0,
                scale: scale_0,
                showHelp: showHelp.pop() == true,
                scaleBinColor: binForecastScale({
                  bins: bins_0,
                  color: color_0,
                  colors
                }),
                showValue: false,
                showScale: mode == "text" ? showScale : false,
                showTick: mode == "text",
                noteCustom,
                label: labelLong ?? label,
                format,
                axisLabelsPlacement: mode == "text" ? "center-tick" : "book-ends"
              } satisfies ForecastBinBarProps & {
                key: string;
              };
            });
            return <>{match(mode).with("bar", () => <LikelihoodBar {...{
                value: layout[0],
                value2: layout[1],
                scale: scale_0,
                baseline: baseline ? {
                  ...baseline,
                  label: hideBaselineLabel ? undefined : "baseline"
                } : undefined,
                relative: shouldVizRelative,
                showScale,
                bgProps: code == "likelihood" && bgColor ? {
                  style: {
                    backgroundColor: `color-mix(in srgb, ${bgColor} 10%, transparent)`
                  }
                } : undefined
              }} />).with("text", () => <div className="">{mainValue != null && <div className="relative inline-block" style={{
                  left: scale_0(mainValue),
                  transform: "translateX(-50%)",
                  color: colors.gray[5]
                }}><span className="font-numbers text-[2.5em] leading-none font-extrabold text-stroke-white">{Fmt.fmt(mainValue, format.absolute.default)}</span></div>}</div>).exhaustive()}<div className="mt-1 space-y-1">{showBinsSameLine ? binBarProps.map(_temp6) : binBarProps.map(_temp7)}</div>{comparisons && <QRComparisons comparisons={comparisons} scale={scale_0} fmt={format.absolute.default} />}</>;
          })}</div></div>}</div>;
    $[0] = cfg;
    $[1] = className;
    $[2] = color;
    $[3] = colors;
    $[4] = comparisons;
    $[5] = containerRef;
    $[6] = hideBaselineLabel;
    $[7] = items;
    $[8] = mode;
    $[9] = noteCustom;
    $[10] = pX;
    $[11] = parentCfg;
    $[12] = showHelpRaw;
    $[13] = showScale;
    $[14] = tag;
    $[15] = width;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  return t1;
};
function _temp(i) {
  return i.forecasts.length == 0;
}
function _temp2() {
  return <></>;
}
function _temp3() {}
function _temp4(b) {
  return b.forecasts.length > 0;
}
function _temp5(a, b_0) {
  return a.i == b_0.i;
}
function _temp6(p) {
  return <ForecastBinBar {...p} />;
}
function _temp7(t0) {
  const {
    key,
    ...p_0
  } = t0;
  return <div key={key}><ForecastBinBar {...p_0} /></div>;
}